import { useMemo, useState } from 'react';
import type { RefObject } from 'react';
import { Camera } from 'lucide-react';
import { Badge } from '@/app/components/ui/badge';
import { Button } from '@/app/components/ui/button';
import { Input } from '@/app/components/ui/input';
import { Label } from '@/app/components/ui/label';
import { Progress } from '@/app/components/ui/progress';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/app/components/ui/tabs';
import { Textarea } from '@/app/components/ui/textarea';
import { ScannerModal } from '@/components/scanner';
import type { ReceiveGearItem } from '@/lib/types';
import { generateSerialRange, parseSerialInput } from '../receive-gear-utils';
import type { ParsedSerialInput } from '../receive-gear-utils';
import type { BatchProgress } from './batch-utils';

export type SerialEntryBatchMethod = 'paste' | 'range';

export interface SerialEntryPanelProps {
  /** Focused input for scanner / keyboard entry; parents refocus it after work. */
  singleInputRef?: RefObject<HTMLInputElement | null>;
  /** One serial from the scan/type box, the camera scanner, or Enter. */
  onSingle: (serial: string) => void;
  /** A list of items from the paste or range tab. */
  onBatch: (items: ReceiveGearItem[], method: SerialEntryBatchMethod) => void | Promise<void>;
  /** True while a request is running; disables the batch buttons. */
  processing?: boolean;
  /** When false, entry is disabled and `disabledPlaceholder` is shown. */
  ready?: boolean;
  disabledPlaceholder?: string;
  /** Verb used on buttons: "Receive", "Add", "Send"… */
  actionVerb?: string;
  /** Noun after the count on buttons: "item" / "items". */
  itemNoun?: { singular: string; plural: string };
  pastePlaceholder?: string;
  /** Text under the range preview, e.g. "will be created" or "will be added". */
  rangeSummarySuffix?: string;
  /** Optional parser override (defaults to the Receive-gear parser). */
  parsePaste?: (text: string) => ParsedSerialInput;
  /** Progress bar under the tabs, shown while `processing` and total > 1. */
  progress?: BatchProgress;
  progressLabel?: string;
  /** Wires the shared required-field validator to the paste/range buttons. */
  validationScope?: string;
  scannerTitle?: string;
  scannerDescription?: string;
  singleHint?: string;
}

/**
 * The three Receive-gear entry methods (scan/type, paste list, sequential
 * range) as one reusable panel. Send gear and Batch inspect feed the same
 * component so every batch screen works the same way with a USB/Bluetooth
 * scanner, a pasted list, or a serial range.
 */
export function SerialEntryPanel({
  singleInputRef,
  onSingle,
  onBatch,
  processing = false,
  ready = true,
  disabledPlaceholder = 'Complete the details above first',
  actionVerb = 'Add',
  itemNoun = { singular: 'item', plural: 'items' },
  pastePlaceholder = 'SN001\nSN002\nSN003',
  rangeSummarySuffix = 'will be added',
  parsePaste = parseSerialInput,
  progress,
  progressLabel = 'Processing',
  validationScope,
  scannerTitle = 'Scan serial number',
  scannerDescription = 'The scanned barcode value is used as the asset serial.',
  singleHint = 'The input clears immediately and remains focused for the next scanner entry.',
}: SerialEntryPanelProps) {
  const [singleSerial, setSingleSerial] = useState('');
  const [scannerOpen, setScannerOpen] = useState(false);
  const [pasteText, setPasteText] = useState('');
  const parsedPaste = useMemo(() => parsePaste(pasteText), [parsePaste, pasteText]);
  const [rangeFirst, setRangeFirst] = useState('');
  const [rangeLast, setRangeLast] = useState('');
  const range = useMemo(() => generateSerialRange(rangeFirst, rangeLast), [rangeFirst, rangeLast]);

  const noun = (count: number) => (count === 1 ? itemNoun.singular : itemNoun.plural);
  const validationProps = validationScope
    ? { 'data-validate-required': 'true', 'data-validation-scope': validationScope }
    : {};

  const submitSingle = (serial: string) => {
    const trimmed = serial.trim();
    setSingleSerial('');
    if (!trimmed) return;
    onSingle(trimmed);
    singleInputRef?.current?.focus();
  };

  return (
    <>
      {/*
        Entry boxes are scratch input: they empty once their lines join the
        batch, so the page's Send / Sign buttons must never treat them as
        required. The boundary keeps page-level validation out of here.
      */}
      <div data-required-validation-boundary="true">
        <Tabs defaultValue="single">
          <TabsList className="grid w-full grid-cols-3">
            <TabsTrigger value="single">Scan / type</TabsTrigger>
            <TabsTrigger value="paste">Paste list</TabsTrigger>
            <TabsTrigger value="range">Range</TabsTrigger>
          </TabsList>

          <TabsContent value="single" className="space-y-4 pt-4">
            <form
              className="space-y-3"
              onSubmit={(event) => {
                event.preventDefault();
                submitSingle(singleSerial);
              }}
            >
              <Label htmlFor="single-serial">Serial number</Label>
              <div className="flex gap-2">
                <Input
                  ref={singleInputRef}
                  id="single-serial"
                  className="font-mono text-lg"
                  placeholder={ready ? 'Scan or type, then press Enter' : disabledPlaceholder}
                  value={singleSerial}
                  onChange={(event) => setSingleSerial(event.target.value)}
                  disabled={!ready}
                  autoComplete="off"
                />
                <Button type="button" variant="outline" onClick={() => setScannerOpen(true)} disabled={!ready}>
                  <Camera className="h-4 w-4 mr-2" />
                  Camera
                </Button>
              </div>
              <p className="text-sm text-gray-500">{singleHint}</p>
            </form>
          </TabsContent>

          <TabsContent value="paste" className="space-y-4 pt-4">
            <div className="space-y-2">
              <Label htmlFor="paste-serials">Serial list</Label>
              <Textarea
                id="paste-serials"
                className="font-mono"
                rows={9}
                placeholder={pastePlaceholder}
                value={pasteText}
                onChange={(event) => setPasteText(event.target.value)}
                disabled={processing || !ready}
              />
              <p className="text-sm font-medium">{parsedPaste.serials.length} serials found</p>
              {parsedPaste.duplicates.length > 0 && (
                <div className="rounded-lg border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900">
                  Duplicates in this list: {parsedPaste.duplicates.join(', ')}
                </div>
              )}
              {parsedPaste.errors.length > 0 && (
                <div className="rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-800 space-y-1">
                  {parsedPaste.errors.map((error) => (
                    <p key={`${error.lineNumber}-${error.line}`}>
                      Line {error.lineNumber}: {error.message}
                    </p>
                  ))}
                </div>
              )}
            </div>
            <Button
              type="button"
              {...validationProps}
              disabled={processing || !ready || parsedPaste.items.length === 0}
              onClick={async () => {
                await onBatch(parsedPaste.items, 'paste');
                // Keep only the lines that could not be parsed so they can be fixed.
                setPasteText(parsedPaste.errors.map((error) => error.line).join('\n'));
              }}
            >
              {actionVerb} {parsedPaste.serials.length || ''} {noun(parsedPaste.serials.length)}
            </Button>
          </TabsContent>

          <TabsContent value="range" className="space-y-4 pt-4">
            <div className="grid gap-4 sm:grid-cols-2">
              <div className="space-y-2">
                <Label htmlFor="range-first">First serial *</Label>
                <Input
                  id="range-first"
                  className="font-mono"
                  placeholder="24A0001"
                  value={rangeFirst}
                  onChange={(event) => setRangeFirst(event.target.value)}
                  disabled={!ready}
                  required
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="range-last">Last serial *</Label>
                <Input
                  id="range-last"
                  className="font-mono"
                  placeholder="24A0900"
                  value={rangeLast}
                  onChange={(event) => setRangeLast(event.target.value)}
                  disabled={!ready}
                  required
                />
              </div>
            </div>
            {range.error && (
              <div className="rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-700">{range.error}</div>
            )}
            {range.serials.length > 0 && (
              <div className="rounded-lg border bg-gray-50 p-4 space-y-2">
                <p className="font-medium">
                  {range.serials.length.toLocaleString()} serials {rangeSummarySuffix}
                </p>
                <div className="flex flex-wrap gap-2 font-mono text-sm">
                  {range.serials.slice(0, 3).map((serial) => (
                    <Badge key={`first-${serial}`} variant="outline">{serial}</Badge>
                  ))}
                  {range.serials.length > 6 && <span className="text-gray-500">…</span>}
                  {range.serials.slice(Math.max(3, range.serials.length - 3)).map((serial) => (
                    <Badge key={`last-${serial}`} variant="outline">{serial}</Badge>
                  ))}
                </div>
              </div>
            )}
            <Button
              type="button"
              {...validationProps}
              disabled={processing || !ready || Boolean(range.error) || range.serials.length === 0}
              onClick={async () => {
                await onBatch(range.serials.map((serial) => ({ serial })), 'range');
                setRangeFirst('');
                setRangeLast('');
              }}
            >
              {actionVerb} {range.serials.length ? range.serials.length.toLocaleString() : ''} {itemNoun.plural}
            </Button>
          </TabsContent>
        </Tabs>
      </div>

      {processing && progress && progress.total > 1 && (
        <div className="mt-6 space-y-2" aria-live="polite">
          <div className="flex justify-between text-sm">
            <span>{progressLabel} {progress.total.toLocaleString()} items…</span>
            <span>{progress.processed.toLocaleString()} / {progress.total.toLocaleString()}</span>
          </div>
          <Progress value={(progress.processed / progress.total) * 100} />
        </div>
      )}

      <ScannerModal
        open={scannerOpen}
        onOpenChange={setScannerOpen}
        onScan={submitSingle}
        title={scannerTitle}
        description={scannerDescription}
      />
    </>
  );
}
